import { Component, createRef } from 'react';
import SakuraOrbit from './SakuraOrbit';
import { Link } from 'react-router-dom';
import SakuraSep from './SakuraSep';
import { DataRPC } from '../../rpc-client/DataRPC';
import { s3 } from '../../config/constants';
import { augment, PALETTE } from './Teams';

const rpc = new DataRPC();
const PAGE_SIZE = 100;

/* Festival year: Apr 30 is the cutover — same year until next Apr 29 */
const festivalYear = () => {
    const now = new Date();
    const m = now.getMonth() + 1; // 1-based
    const d = now.getDate();
    const y = now.getFullYear();
    return String(m > 4 || (m === 4 && d >= 30) ? y : y - 1);
};

/* Extract YouTube video ID from a URL or bare ID */
const ytId = (link) => {
    if (!link) return '';
    const m = link.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : link;
};

const ZoomIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/>
    </svg>
);

class TeamsDetails extends Component {
    state = {
        team:              this.props.location?.state?.team || null,
        teamLoading:       !this.props.location?.state?.team,
        vidIdx:            0,
        videos:            [],
        vidLoading:        false,
        filterYear:        festivalYear(),
        filterFest:        null, /* null until festivals load, then a festival ID (number) */
        festivalOptions:   [],
        allYears:          [],
        // tagged pictures
        teamPics:          [],
        teamPicsTotal:     0,
        teamPicsPage:      1,
        teamPicsAll:       null, /* every pic of the year when merging festivals client-side; null = server paging */
        teamPicsLoading:   true,
        teamPicsLoadingPage: false,
        // lightbox
        lbOpen:  false,
        lbIdx:   0,
        // share
        shareCopied: false,
        // yearly videos
        yearlyVideos: [],
    };

    picsSectionRef = createRef();
    _scrollRaf  = null;

    componentDidMount() {
        if (!this.state.team) {
            const slug = this.props.match?.params?.slug;
            if (slug) {
                const isNumeric = /^\d+$/.test(slug);
                const params = isNumeric ? { id: Number(slug) } : { slug };
                rpc.fetchRawData('GetTeam1', params)
                    .then(res => {
                        const raw = res || {};
                        const idx = (raw.id || 0) % PALETTE.length;
                        this.setState({ team: augment(raw, idx), teamLoading: false });
                    })
                    .catch(() => this.setState({ teamLoading: false }));
            } else {
                this.setState({ teamLoading: false, teamPicsLoading: false });
            }
        } else {
            this.fetchFestivalsForYear(this.state.filterYear);
            this.fetchYearlyVideos(this.state.team.id);
        }

        this.onKeyDown = (e) => {
            const { lbOpen, lbIdx, teamPics } = this.state;
            if (!lbOpen) return;
            const n = teamPics.length;
            if (e.key === 'Escape')     this.closeLb();
            if (e.key === 'ArrowLeft')  this.setState({ lbIdx: (lbIdx - 1 + n) % n });
            if (e.key === 'ArrowRight') this.setState({ lbIdx: (lbIdx + 1) % n });
        };
        window.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
        document.body.style.overflow = '';
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevState.team && this.state.team) {
            this.fetchFestivalsForYear(this.state.filterYear);
            this.fetchYearlyVideos(this.state.team.id);
        }
    }

    fetchYearlyVideos = (teamId) => {
        if (!teamId) return;
        rpc.fetchRawData('GetTeamYearlyVideos', { team_id: teamId })
            .then(res => {
                const list = Array.isArray(res) ? res : [];
                list.sort((a, b) => Number(b.year) - Number(a.year));
                this.setState({ yearlyVideos: list });
            })
            .catch(() => {});
    };

    fetchFestivalsForYear = (year) => {
        rpc.fetchRawData('GetFestivals', { page: 1, limit: 100 })
            .then(res => {
                const all  = (res.data || []);
                const allYears = [...new Set(all.map(f => String(new Date(f.start_date + 'T00:00:00').getFullYear())))].sort().reverse();

                let opts = all;
                if (year !== 'all') {
                    const y = parseInt(year, 10);
                    opts = all.filter(f => new Date(f.start_date + 'T00:00:00').getFullYear() === y);
                }

                opts.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

                this.setState({ festivalOptions: opts, allYears }, () => this.selectFestival(null));
            })
            .catch(() => {});
    };

    selectFestival = (festId) => {
        const team = this.state.team;
        this.setState({
            filterFest:          festId,
            vidIdx:              0,
            videos:              [],
            vidLoading:          !!team?.id,
            teamPics:            [],
            teamPicsPage:        1,
            teamPicsAll:         null,
            teamPicsLoading:     !!team?.id,
        }, () => {
            if (team?.id) {
                this.fetchVideos(festId);
                this.fetchTeamPics(1);
            }
        });
    };

    fetchVideos = (festId) => {
        const team = this.state.team;
        if (!team?.id) { this.setState({ vidLoading: false }); return; }
        const params = { team_id: team.id };
        if (festId) params.festival_id = festId;
        rpc.fetchRawData('GetVideos', params)
            .then(res => {
                let videos = res || [];
                if (!festId) {
                    const { festivalOptions, filterYear } = this.state;
                    if (filterYear !== 'all' && festivalOptions.length > 0) {
                        const validIds = new Set(festivalOptions.map(f => f.id));
                        videos = videos.filter(v => !v.festival_id || validIds.has(v.festival_id));
                    }
                }
                this.setState({ videos, vidLoading: false });
            })
            .catch(() => this.setState({ vidLoading: false }));
    };

    fetchTeamPics = (page) => {
        const team = this.state.team;
        if (!team?.id) return;
        const { filterFest, festivalOptions, filterYear } = this.state;
        const isFirst = page === 1;
        this.setState(isFirst ? { teamPicsLoading: true } : { teamPicsLoadingPage: true });

        /* When "Tất Cả Sự Kiện" + specific year: fetch per-festival in parallel, merge, then page client-side */
        if (!filterFest && filterYear !== 'all' && festivalOptions.length > 0) {
            Promise.all(
                festivalOptions.map(f =>
                    rpc.fetchRawData('GetPictures', { team_id: team.id, festival_id: f.id, page: 1, limit: 500 })
                        .then(res => res?.data ?? [])
                        .catch(() => [])
                )
            ).then(results => {
                const all = results.flat();
                this.setState({
                    teamPicsAll:         all,
                    teamPics:            all.slice(0, PAGE_SIZE),
                    teamPicsTotal:       all.length,
                    teamPicsPage:        1,
                    teamPicsLoading:     false,
                    teamPicsLoadingPage: false,
                });
            });
            return;
        }

        const params = { team_id: team.id, page, limit: PAGE_SIZE };
        if (filterFest) params.festival_id = filterFest;
        rpc.fetchRawData('GetPictures', params)
            .then(res => {
                this.setState({
                    teamPics:            res?.data  ?? [],
                    teamPicsTotal:       res?.total ?? 0,
                    teamPicsPage:        page,
                    teamPicsLoading:     false,
                    teamPicsLoadingPage: false,
                });
            })
            .catch(() => this.setState({ teamPicsLoading: false, teamPicsLoadingPage: false }));
    };

    goToPicsPage = (p) => {
        const { teamPicsTotal, teamPicsPage, teamPicsAll, teamPicsLoadingPage } = this.state;
        const totalPages = Math.ceil(teamPicsTotal / PAGE_SIZE);
        if (p < 1 || p > totalPages || p === teamPicsPage || teamPicsLoadingPage) return;

        const top = this.picsSectionRef.current?.getBoundingClientRect().top;
        if (top !== undefined) window.scrollTo({ top: window.scrollY + top - 100, behavior: 'smooth' });

        if (teamPicsAll) {
            this.setState({ teamPics: teamPicsAll.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE), teamPicsPage: p });
        } else {
            this.fetchTeamPics(p);
        }
    };

    renderPicsPagination(totalPages) {
        const page = this.state.teamPicsPage;
        if (totalPages <= 1) return null;

        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }

        return (
            <div className="alb-pagination">
                <button className="alb-pg-btn alb-pg-prev" onClick={() => this.goToPicsPage(page - 1)} disabled={page === 1}>‹</button>
                {pages.map((p, i) =>
                    p === '...'
                        ? <span key={`e${i}`} className="alb-pg-ellipsis">…</span>
                        : <button key={p} className={`alb-pg-btn${p === page ? ' alb-pg-btn--active' : ''}`} onClick={() => this.goToPicsPage(p)}>{p}</button>
                )}
                <button className="alb-pg-btn alb-pg-next" onClick={() => this.goToPicsPage(page + 1)} disabled={page === totalPages}>›</button>
            </div>
        );
    }

    goVid = (dir) => {
        const { videos } = this.state;
        const n = videos.length || 1;
        this.setState(prev => ({ vidIdx: (prev.vidIdx + dir + n) % n }));
    };

    openLb = (i) => {
        this.setState({ lbOpen: true, lbIdx: i });
        document.body.style.overflow = 'hidden';
    };

    closeLb = () => {
        this.setState({ lbOpen: false });
        document.body.style.overflow = '';
    };

    lbNav = (dir) => {
        const { teamPics } = this.state;
        this.setState(prev => ({
            lbIdx: (prev.lbIdx + dir + teamPics.length) % teamPics.length,
        }));
    };

    handleShare = () => {
        const url  = window.location.href;
        const { team } = this.state;
        if (navigator.share) {
            navigator.share({ title: team?.name || 'Yosakoi Team', url });
            return;
        }
        navigator.clipboard.writeText(url).then(() => {
            this.setState({ shareCopied: true });
            setTimeout(() => this.setState({ shareCopied: false }), 2000);
        });
    };

    render() {
        const { team, teamLoading } = this.state;

        if (teamLoading) {
            return <SakuraOrbit label="Đang tải thông tin đội" />;
        }

        if (!team) {
            return (
                <div className="tmd-not-found">
                    <div className="tmd-not-found-kanji">桜</div>
                    <h2>Không tìm thấy đội</h2>
                    <p>Vui lòng chọn một đội từ trang Đội Nhóm.</p>
                    <Link to="/teams" className="tmd-back-btn">← Về Đội Nhóm</Link>
                </div>
            );
        }

        const {
            vidIdx, videos, vidLoading,
            filterYear, filterFest, festivalOptions, allYears,
            teamPics, teamPicsTotal, teamPicsPage, teamPicsLoading, teamPicsLoadingPage,
            lbOpen, lbIdx,
            yearlyVideos,
        } = this.state;

        const years      = allYears;
        const safeVidIdx = Math.min(vidIdx, Math.max(videos.length - 1, 0));
        const curVid     = videos[safeVidIdx] || null;
        const curPhoto   = lbOpen ? teamPics[lbIdx] : null;
        const picsPages  = Math.ceil(teamPicsTotal / PAGE_SIZE);
        const picsOffset = (teamPicsPage - 1) * PAGE_SIZE;

        const MIN_CYCLE  = 8;
        const yvBase     = yearlyVideos.length > 0
            ? Array.from({ length: Math.ceil(MIN_CYCLE / yearlyVideos.length) }, () => yearlyVideos).flat()
            : [];
        const yvDisplayed   = [...yvBase, ...yvBase];
        const yvDuration    = `${Math.max(16, yvBase.length * 4)}s`;

        return (
            <div className="tmd-page">

                {/* Page header */}
                <div
                    className="page-header tmd-page-header"
                    style={team.background_url ? {
                        backgroundImage: `url(${s3(team.background_url)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    } : {}}
                >
                    <div className="page-header-overlay tmd-overlay" style={{ background: team.background_url ? 'rgba(10,5,20,.52)' : team.gradient }} />
                    <div className="container page-header-body">
                        <div className="row">
                            <div className="col-12">
                                <div className="entry-meta-date">{team.city} · Thành lập {team.established}</div>
                                <h1 className="page-header-title">{team.name}</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sep-gold-rule" style={{margin: 0}} />

                <div className="tmd-body">
                    <div className="container">

                        {/* Identity strip */}
                        <div className="tmd-identity">
                            <div className="tmd-identity-avatar-col">
                                <div className="tmd-identity-avatar" style={{ background: team.avatar_url ? undefined : team.gradient }}>
                                    {team.avatar_url
                                        ? <img src={s3(team.avatar_url)} alt={team.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                        : team.initials}
                                </div>
                                <button
                                    className={`tmd-share-btn${this.state.shareCopied ? ' tmd-share-btn--copied' : ''}`}
                                    onClick={this.handleShare}
                                    title="Chia sẻ trang này"
                                >
                                    {this.state.shareCopied ? (
                                        <>
                                            <svg viewBox="0 0 20 20" fill="none" width="15" height="15"><path d="M4 10l5 5L16 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            Đã copy!
                                        </>
                                    ) : (
                                        <>
                                            <svg viewBox="0 0 20 20" fill="none" width="15" height="15"><circle cx="15" cy="4" r="2" stroke="currentColor" strokeWidth="1.8"/><circle cx="15" cy="16" r="2" stroke="currentColor" strokeWidth="1.8"/><circle cx="5" cy="10" r="2" stroke="currentColor" strokeWidth="1.8"/><path d="M7 9l6-3.5M7 11l6 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                                            Chia sẻ
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="tmd-identity-info">
                                <h2 className="tmd-identity-name" style={{ color: team.color1 }}>{team.name}</h2>
                                <p className="tmd-identity-desc" dangerouslySetInnerHTML={{ __html: team.desc }} />
                                <div className="tmd-identity-meta">
                                    <span><strong>Địa điểm:</strong> {team.location}</span>
                                    {team.contact && (
                                        <span><strong>Liên hệ:</strong> {/^https?:\/\//.test(team.contact)
                                            ? <a href={team.contact} target="_blank" rel="noopener noreferrer">{team.contact}</a>
                                            : team.contact}
                                        </span>
                                    )}
                                </div>
                                
                            </div>
                        </div>

                        <div className="tmd-media-panel">

                            {/* Filter bar (videos only) */}
                            <div className="gal-filter-bar">
                                <div className="gal-filter-select-wrap">
                                    <label className="gal-filter-label" htmlFor="tmd-filter-year">Năm</label>
                                    <div className="gal-select-box">
                                        <select
                                            id="tmd-filter-year"
                                            value={filterYear}
                                            onChange={e => this.setState({ filterYear: e.target.value, vidIdx: 0 }, () => this.fetchFestivalsForYear(this.state.filterYear))}
                                        >
                                            <option value="all">Tất cả năm</option>
                                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                                        </select>
                                        <span className="gal-select-arrow" />
                                    </div>
                                </div>

                                <div className="gal-filter-select-wrap">
                                    <label className="gal-filter-label" htmlFor="tmd-filter-fest">Sự kiện</label>
                                    <div className="gal-select-box">
                                        <select
                                            id="tmd-filter-fest"
                                            value={filterFest ?? ''}
                                            onChange={e => this.selectFestival(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                        >
                                            <option value="">Tất Cả Sự Kiện</option>
                                            {festivalOptions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                        </select>
                                        <span className="gal-select-arrow" />
                                    </div>
                                </div>

                                {filterYear !== festivalYear() && (
                                    <button
                                        className="gal-filter-reset"
                                        onClick={() => this.setState({ filterYear: festivalYear() }, () => this.fetchFestivalsForYear(this.state.filterYear))}
                                    >✕ Xóa bộ lọc</button>
                                )}

                                <span className="tmd-filter-summary">
                                    {videos.length} video · {teamPicsTotal} ảnh
                                </span>
                            </div>

                            {/* Video slideshow */}
                            <div className="tmd-video-col">
                                {yvDisplayed.length > 0 && (
                                    <div className="tmd-yearly-vids-wrap">
                                        <div
                                            className="tmd-yearly-vids"
                                            style={{ animationDuration: yvDuration }}
                                        >
                                            {yvDisplayed.map((v, i) => (
                                                <button
                                                    key={i}
                                                    className={`tmd-year-vid-thumb${String(v.year) === String(filterYear) ? ' tmd-year-vid-thumb--active' : ''}`}
                                                    onClick={() => {
                                                        const yr = String(v.year);
                                                        this.setState({ filterYear: yr }, () => this.fetchFestivalsForYear(yr));
                                                    }}
                                                    title={v.name}
                                                >
                                                    <div className="tmd-year-vid-thumb-img">
                                                        <img
                                                            src={`https://img.youtube.com/vi/${ytId(v.youtube_link)}/mqdefault.jpg`}
                                                            alt={v.name}
                                                            loading="lazy"
                                                        />
                                                        <span className="tmd-year-vid-thumb-play">
                                                            <svg viewBox="0 0 20 20" fill="currentColor" width="22" height="22"><path d="M6.5 5.5l8 4.5-8 4.5V5.5z"/></svg>
                                                        </span>
                                                    </div>
                                                    <span className="tmd-year-vid-year">{v.year}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="entry-title">
                                    <p>VIDEO TRÌNH DIỄN</p>
                                    <h2></h2>
                                </div>
                                {vidLoading ? (
                                    <SakuraOrbit label="Đang tải video" size="sm" />
                                ) : videos.length === 0 ? (
                                    <div className="gal-no-results">
                                        <div className="gal-no-results-kanji">映</div>
                                        <p>Chưa có video nào cho lễ hội này</p>
                                    </div>
                                ) : (
                                    <div className="gallery-video-wrap">
                                        <div className="gallery-video-meta">
                                            {/* <span className="gallery-video-label">チーム · Team Performances</span> */}
                                            <span className="gallery-video-count">{safeVidIdx + 1} / {videos.length}</span>
                                        </div>
                                        <div className="gallery-video-slideshow">
                                            <button className="gallery-vid-nav" onClick={() => this.goVid(-1)} title="Trước (←)">&#8249;</button>
                                            <div className="gallery-video-frame">
                                                <iframe
                                                    key={curVid.id + vidIdx}
                                                    src={`https://www.youtube.com/embed/${ytId(curVid.youtube_link)}?rel=0&modestbranding=1`}
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    title={curVid.name}
                                                />
                                            </div>
                                            <button className="gallery-vid-nav" onClick={() => this.goVid(1)} title="Tiếp (→)">&#8250;</button>
                                        </div>
                                        <div className="gallery-video-info">
                                            <h4>{curVid.name}</h4> <br/>
                                            <p dangerouslySetInnerHTML={{ __html: curVid.description }} />
                                        </div>
                                        <div className="gallery-vid-dots">
                                            {videos.map((_, i) => (
                                                <button
                                                    key={i}
                                                    className={`gallery-vid-dot${i === safeVidIdx ? ' active' : ''}`}
                                                    onClick={() => this.setState({ vidIdx: i })}
                                                    aria-label={`Video ${i + 1}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Tagged pictures section */}
                            <div className="tmd-section" ref={this.picsSectionRef}>
                                <div className="entry-title">
                                    <p>BỘ ẢNH</p>
                                    <h2>
                                        KHOẢNH KHẮC LỄ HỘI
                                        {teamPicsTotal > 0 && (
                                            <span className="alb-count-pill">{picsOffset + 1}–{picsOffset + teamPics.length} / {teamPicsTotal}</span>
                                        )}
                                    </h2>
                                </div>

                                {teamPicsLoading ? (
                                    <SakuraOrbit label="Đang tải ảnh" />
                                ) : teamPics.length === 0 ? (
                                    <div className="gal-no-results">
                                        <div className="gal-no-results-kanji">桜</div>
                                        <p>Chưa có ảnh nào được gắn tag cho đội này</p>
                                    </div>
                                ) : (
                                    <>
                                        {this.renderPicsPagination(picsPages)}
                                        <div className={`alb-pics-grid${teamPicsLoadingPage ? ' alb-pics-grid--loading' : ''}`}>
                                            {teamPics.map((pic, i) => (
                                                <div key={pic.id} className="alb-pic-item" onClick={() => this.openLb(i)}>
                                                    <img
                                                        src={pic.thumbnail_url || pic.image_url}
                                                        alt={pic.caption || ''}
                                                        loading="lazy"
                                                    />
                                                    <div className="alb-pic-overlay">
                                                        <ZoomIcon />
                                                    </div>
                                                    {pic.caption && <div className="alb-pic-caption">{pic.caption}</div>}
                                                </div>
                                            ))}
                                        </div>

                                        {this.renderPicsPagination(picsPages)}
                                        {picsPages > 1 && (
                                            <div className="alb-pg-info">Trang {teamPicsPage} / {picsPages} · {teamPicsTotal} ảnh</div>
                                        )}
                                    </>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Lightbox */}
                {lbOpen && curPhoto && (
                    <div className="gallery-lightbox active" role="dialog" aria-modal="true">
                        <div className="gallery-lightbox-backdrop" onClick={this.closeLb} />
                        <button className="gallery-lb-close" onClick={this.closeLb} title="Đóng (Esc)">&#10005;</button>
                        <button className="gallery-lb-nav prev" onClick={() => this.lbNav(-1)} title="Trước">&#8249;</button>
                        <button className="gallery-lb-nav next" onClick={() => this.lbNav(1)}  title="Tiếp">&#8250;</button>
                        <div className="gallery-lightbox-content">
                            <img src={curPhoto.image_url || curPhoto.thumbnail_url} alt={curPhoto.caption || ''} />
                            {curPhoto.caption && <p className="gallery-lb-caption">{curPhoto.caption}</p>}
                            <p className="gallery-lb-counter">{picsOffset + lbIdx + 1} / {teamPicsTotal || teamPics.length}</p>
                        </div>
                    </div>
                )}

                <SakuraSep />
                <div className="sep-gold-rule" style={{margin: 0}} />

            </div>
        );
    }
}

export default TeamsDetails;
